import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    CircularProgress,
    Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import MapIcon from '@mui/icons-material/Map';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import StraightenIcon from '@mui/icons-material/Straighten';
import LayersIcon from '@mui/icons-material/Layers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { analysisService, pdfService } from '../../services';
import { AnalysisResult } from '../../types';
import { formatArea, formatDistance, formatCoordinate, formatDate } from '../../utils/formatters';

// Summary card component
interface SummaryCardProps {
    icon: React.ElementType;
    label: string;
    value: string;
    color?: string;
}

const SummaryCard = ({ icon: Icon, label, value, color = 'primary.main' }: SummaryCardProps) => (
    <Card
        sx={{
            height: '100%',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                }}
            >
                <Icon sx={{ fontSize: 28, color }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color, mb: 0.5 }}>
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </CardContent>
    </Card>
);

const ResultsPage = () => {
    const navigate = useNavigate();
    const [result, setResult] = useState<AnalysisResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load latest result on mount
    useEffect(() => {
        const loadResult = async () => {
            try {
                const latestResult = await analysisService.getLatestResult();
                if (latestResult) {
                    setResult(latestResult);
                } else {
                    setError('No analysis results. Please upload a file first.');
                }
            } catch {
                setError('Failed to load analysis results.');
            } finally {
                setIsLoading(false);
            }
        };

        loadResult();
    }, []);

    const handleDownloadPdf = async () => {
        if (!result) return;

        setIsGeneratingPdf(true);
        try {
            const pdfResult = await pdfService.generatePdf(result);
            pdfService.downloadPdf(pdfResult);
        } catch {
            setError('Failed to generate PDF. Please try again.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleNewAnalysis = () => {
        navigate('/upload');
    };

    // Get severity icon
    const getSeverityIcon = (severity?: string) => {
        switch (severity) {
            case 'warning':
                return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
            case 'important':
                return <LightbulbIcon sx={{ color: 'secondary.main', fontSize: 20 }} />;
            default:
                return <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />;
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (error || !result?.data) {
        return (
            <Box
                sx={{
                    minHeight: '80vh',
                    py: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Container maxWidth="sm">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {error || 'No analysis data.'}
                    </Alert>
                    <Box sx={{ textAlign: 'center' }}>
                        <Button variant="contained" onClick={() => navigate('/upload')}>
                            Upload New File
                        </Button>
                    </Box>
                </Container>
            </Box>
        );
    }

    const { data } = result;

    return (
        <Box
            sx={{
                minHeight: '80vh',
                py: { xs: 4, md: 6 },
                backgroundColor: 'grey.50',
            }}
        >
            <Container maxWidth="lg">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                            Analysis Results
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {formatDate(result.createdAt)} • ID: {result.id.slice(0, 12)}...
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {/* Left Column - Map & Summary */}
                        <Grid item xs={12} lg={8}>
                            {/* Map Preview */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    mb: 4,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <MapIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Map Preview
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        paddingTop: '60%',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backgroundColor: 'grey.100',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={data.mapImage}
                                        alt="Map Preview"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        size="small"
                                        label={data.summary.coordinateSystem}
                                        variant="outlined"
                                        color="primary"
                                    />
                                    <Chip
                                        size="small"
                                        label={`${data.summary.parcelCount} bidang`}
                                        variant="outlined"
                                    />
                                </Box>
                            </Paper>

                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={6} md={3}>
                                    <SummaryCard
                                        icon={SquareFootIcon}
                                        label="Total Area"
                                        value={formatArea(data.summary.totalArea)}
                                        color="#1976d2"
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <SummaryCard
                                        icon={StraightenIcon}
                                        label="Perimeter"
                                        value={formatDistance(data.summary.perimeter)}
                                        color="#9c27b0"
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <SummaryCard
                                        icon={LayersIcon}
                                        label="Feature Count"
                                        value={`${data.summary.parcelCount}`}
                                        color="#2e7d32"
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <SummaryCard
                                        icon={LocationOnIcon}
                                        label="Center Point"
                                        value={`${data.summary.centerPoint.lat.toFixed(4)}°`}
                                        color="#ed6c02"
                                    />
                                </Grid>
                            </Grid>

                            {/* Parcel Details */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                    Feature Details
                                </Typography>
                                <Box sx={{ overflow: 'auto' }}>
                                    <Box
                                        component="table"
                                        sx={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            '& th, & td': {
                                                p: 2,
                                                textAlign: 'left',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            },
                                            '& th': {
                                                fontWeight: 600,
                                                backgroundColor: 'grey.50',
                                            },
                                            '& tr:hover td': {
                                                backgroundColor: 'grey.50',
                                            },
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Area</th>
                                                <th>Perimeter</th>
                                                <th>Zoning</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.parcels.map((parcel, index) => (
                                                <tr key={parcel.id}>
                                                    <td>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {parcel.name || `Bidang ${index + 1}`}
                                                        </Typography>
                                                    </td>
                                                    <td>{formatArea(parcel.area)}</td>
                                                    <td>{formatDistance(parcel.perimeter)}</td>
                                                    <td>
                                                        <Chip
                                                            size="small"
                                                            label={(parcel.properties.zoning as string) || '-'}
                                                            variant="outlined"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Right Column - Interpretation & Actions */}
                        <Grid item xs={12} lg={4}>
                            {/* Actions */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    mb: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    color: 'white',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Download Report
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                                    Download complete analysis results in professional PDF format.
                                </Typography>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    startIcon={isGeneratingPdf ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                    onClick={handleDownloadPdf}
                                    disabled={isGeneratingPdf}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: 'primary.main',
                                        mb: 2,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                        },
                                    }}
                                >
                                    {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<AddIcon />}
                                    onClick={handleNewAnalysis}
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        '&:hover': {
                                            borderColor: 'white',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                        },
                                    }}
                                >
                                    New Analysis
                                </Button>
                            </Paper>

                            {/* Interpretation */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    mb: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Interpretation
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {data.interpretation.summary}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                {data.interpretation.details.map((detail, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            gap: 1.5,
                                            mb: 2,
                                            '&:last-child': { mb: 0 },
                                        }}
                                    >
                                        {getSeverityIcon(detail.severity)}
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {detail.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {detail.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>

                            {/* Recommendations */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Recommendations
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                    {data.interpretation.recommendations.map((rec, index) => (
                                        <Box
                                            component="li"
                                            key={index}
                                            sx={{
                                                mb: 1.5,
                                                '&:last-child': { mb: 0 },
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {rec}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>

                            {/* Coordinates Info */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    mt: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.50',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Center Coordinates
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {formatCoordinate(data.summary.centerPoint.lat, data.summary.centerPoint.lng)}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
};

export default ResultsPage;
